import { useState, useCallback } from 'react';
import { X, Upload, FileText, Loader2, CheckCircle, AlertCircle, Sparkles } from 'lucide-react';
import { toast } from 'sonner';
import api from '@/lib/api';

export default function ResumeUploadModal({ candidateId, onClose, onSuccess }) {
  const [file, setFile] = useState(null);
  const [uploading, setUploading] = useState(false);
  const [result, setResult] = useState(null);
  const [dragActive, setDragActive] = useState(false);

  const handleDrag = useCallback((e) => {
    e.preventDefault();
    e.stopPropagation();
    if (e.type === 'dragenter' || e.type === 'dragover') {
      setDragActive(true);
    } else if (e.type === 'dragleave') {
      setDragActive(false);
    }
  }, []);

  const handleDrop = useCallback((e) => {
    e.preventDefault();
    e.stopPropagation();
    setDragActive(false);
    
    const droppedFile = e.dataTransfer.files[0];
    if (droppedFile) {
      handleFileSelect(droppedFile);
    }
  }, []);

  const handleFileSelect = (selectedFile) => {
    const validTypes = ['application/pdf', 'application/msword', 'application/vnd.openxmlformats-officedocument.wordprocessingml.document', 'text/plain'];
    const maxSize = 10 * 1024 * 1024; // 10MB

    if (!validTypes.includes(selectedFile.type) && !selectedFile.name.match(/\.(pdf|doc|docx|txt)$/i)) {
      toast.error('Invalid file type. Use PDF, DOC, DOCX, or TXT');
      return;
    }

    if (selectedFile.size > maxSize) {
      toast.error('File too large (max 10MB)');
      return;
    }

    setFile(selectedFile);
  };

  const handleUpload = async () => {
    if (!file) return;

    setUploading(true);
    try {
      const formData = new FormData();
      formData.append('file', file);

      const { data } = await api.post(`/candidates/${candidateId}/upload-resume`, formData, {
        headers: { 'Content-Type': 'multipart/form-data' },
      });

      setResult(data);
      toast.success('Resume scored successfully!');
      setTimeout(() => onSuccess(), 2000);
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to upload resume');
    } finally {
      setUploading(false);
    }
  };

  return (
    <div className="fixed inset-0 bg-slate-900/40 backdrop-blur-sm z-50 flex items-center justify-center p-4 sm:p-6 animate-in fade-in duration-300" onClick={onClose}>
      <div
        className="bg-white/95 backdrop-blur-xl rounded-3xl shadow-[0_20px_60px_-15px_rgba(0,0,0,0.3)] border border-slate-200/60 w-full max-w-2xl overflow-hidden transform transition-all"
        onClick={(e) => e.stopPropagation()}
        data-testid="resume-upload-modal"
      >
        {/* Header */}
        <div className="bg-gradient-to-r from-slate-50 to-white px-6 py-5 border-b border-slate-200/60 flex justify-between items-center">
          <div>
            <h2 className="text-xl font-bold bg-gradient-to-r from-slate-900 to-slate-700 bg-clip-text text-transparent">Upload Resume</h2>
            <p className="text-sm text-slate-500 mt-0.5">AI will parse and score automatically</p>
          </div>
          <button onClick={onClose} className="p-2 text-slate-400 hover:text-slate-600 hover:bg-slate-100 rounded-xl transition-colors">
            <X className="w-5 h-5" />
          </button>
        </div>

        {/* Body */}
        <div className="p-6">
          {!result ? (
            <div>
              {/* Drag and Drop Zone */}
              <div
                onDragEnter={handleDrag}
                onDragLeave={handleDrag}
                onDragOver={handleDrag}
                onDrop={handleDrop}
                className={`border-2 border-dashed rounded-xl p-12 text-center transition-all ${
                  dragActive
                    ? 'border-[#2563eb] bg-blue-50'
                    : 'border-slate-200 hover:border-slate-300 hover:bg-slate-50'
                }`}
              >
                {file ? (
                  <div className="flex flex-col items-center gap-3">
                    <div className="w-16 h-16 bg-green-50 rounded-full flex items-center justify-center">
                      <FileText className="w-8 h-8 text-green-600" />
                    </div>
                    <div>
                      <p className="text-sm font-medium text-slate-800">{file.name}</p>
                      <p className="text-xs text-slate-500">{(file.size / 1024).toFixed(1)} KB</p>
                    </div>
                    <button
                      onClick={() => setFile(null)}
                      className="text-xs text-slate-500 hover:text-red-500 underline"
                    >
                      Remove
                    </button>
                  </div>
                ) : (
                  <div>
                    <div className="w-16 h-16 bg-slate-50 rounded-full flex items-center justify-center mx-auto mb-4">
                      <Upload className="w-8 h-8 text-slate-400" />
                    </div>
                    <p className="text-slate-700 font-medium mb-2">Drag and drop resume here</p>
                    <p className="text-sm text-slate-500 mb-4">or</p>
                    <label className="inline-block">
                      <input
                        type="file"
                        accept=".pdf,.doc,.docx,.txt"
                        onChange={(e) => e.target.files[0] && handleFileSelect(e.target.files[0])}
                        className="hidden"
                      />
                      <span className="inline-block px-5 py-2.5 bg-[#2563eb] hover:bg-[#1d4ed8] text-white rounded-lg text-sm font-semibold cursor-pointer transition-colors">
                        Browse Files
                      </span>
                    </label>
                    <p className="text-xs text-slate-400 mt-4">PDF, DOC, DOCX, TXT (max 10MB)</p>
                  </div>
                )}
              </div>

              {/* Upload Button */}
              {file && !result && (
                <button
                  onClick={handleUpload}
                  disabled={uploading}
                  className="w-full mt-4 bg-gradient-to-r from-blue-600 to-emerald-600 text-white py-3.5 rounded-xl font-bold shadow-lg hover:shadow-xl hover:scale-[1.02] active:scale-[0.98] transition-all disabled:opacity-50 flex items-center justify-center gap-2"
                  data-testid="upload-resume-submit-btn"
                >
                  {uploading ? (
                    <>
                      <Loader2 className="w-4 h-4 animate-spin" />
                      Processing with AI...
                    </>
                  ) : (
                    <>
                      <Sparkles className="w-4 h-4" />
                      Upload & Score with AI
                    </>
                  )}
                </button>
              )}
            </div>
          ) : (
            /* Results Display */
            (() => {
              const safeResumeScore = typeof result?.resume_score === 'object'
                ? result?.resume_score?.score ?? null
                : typeof result?.resume_score === 'number'
                  ? result.resume_score
                  : null;
              const safeResumeSummary = result?.resume_score?.summary || result?.summary || null;
              // eslint-disable-next-line no-unused-vars
              const safeResumeBreakdown = result?.resume_score?.breakdown || result?.breakdown || null;

              return (
                <div className="space-y-4">
                  <div className="text-center py-4">
                    <div className="w-16 h-16 bg-green-50 rounded-full flex items-center justify-center mx-auto mb-4">
                      <CheckCircle className="w-8 h-8 text-green-600" />
                    </div>
                    <h3 className="text-xl font-bold text-slate-800 mb-2">Resume Scored!</h3>
                    <div className="flex items-center justify-center gap-6 mb-4">
                      <div>
                        <p className="text-xs text-slate-500">Resume Score</p>
                        <p className="text-3xl font-bold text-[#2563eb]" data-testid="upload-result-score">
                          {safeResumeScore ?? '—'}
                        </p>
                      </div>
                      <div className="w-px h-12 bg-slate-200" />
                      <div>
                        <p className="text-xs text-slate-500">LinkedIn Score</p>
                        <p className="text-3xl font-bold text-slate-400">{result.linkedin_score || 'N/A'}</p>
                      </div>
                    </div>
                    {safeResumeSummary && (
                      <p className="text-sm text-slate-600 leading-relaxed max-w-md mx-auto">
                        {safeResumeSummary}
                      </p>
                    )}
                  </div>

                  {result.gap_skills?.length > 0 && (
                    <div className="bg-amber-50 border border-amber-200 rounded-xl p-4">
                      <div className="flex items-start gap-2">
                        <AlertCircle className="w-4 h-4 text-amber-600 flex-shrink-0 mt-0.5" />
                        <div>
                          <p className="text-sm font-semibold text-amber-800 mb-1">Gap Skills</p>
                          <p className="text-xs text-amber-700">{result.gap_skills.join(', ')}</p>
                        </div>
                      </div>
                    </div>
                  )}

                  <button
                    onClick={onClose}
                    className="w-full bg-slate-100 hover:bg-slate-200 text-slate-700 py-3 rounded-xl text-sm font-semibold transition-colors"
                  >
                    Close
                  </button>
                </div>
              );
            })()
          )}
        </div>
      </div>
    </div>
  );
}
